<template>
  <div class="weekly-view-hour-premises w-full max-w-full min-w-0">
    <p class="mb-3 text-sm text-grey-dark">
      {{ $t('hour_premises_intro') }}
    </p>

    <div class="weekly-view-hour-premises__table max-w-xl overflow-hidden rounded border border-grey-light">
      <div class="grid grid-cols-2 bg-green-darker text-white">
        <div class="px-4 py-2 text-sm font-bold uppercase tracking-wide">
          {{ $t('premises') }}
        </div>
        <div class="px-4 py-2 text-right text-sm font-bold uppercase tracking-wide">
          {{ $t('calculations') }}
        </div>
      </div>

      <div
        v-for="row in inputRows"
        :key="row.key"
        class="grid grid-cols-2 items-center border-b border-grey-light bg-white"
      >
        <div class="px-4 py-3 text-sm font-bold uppercase text-grey-darkest">
          {{ $t(row.labelKey) }}
        </div>
        <div class="flex items-center justify-end px-4 py-2">
          <input
            v-model.number="form[row.key]"
            type="number"
            min="0"
            :max="row.showPercent ? 100 : undefined"
            step="any"
            class="weekly-view-hour-premises__input w-24 rounded border border-grey px-2 py-1 text-center font-semibold text-green-darker"
            :disabled="saving"
          >
          <span
            v-if="row.showPercent"
            class="ml-2 w-4 text-sm font-semibold text-grey-darkest"
          >%</span>
          <span
            v-else
            class="ml-2 w-4"
            aria-hidden="true"
          />
        </div>
      </div>

      <div class="grid grid-cols-2 items-center border-t-2 border-green-dark bg-green-lightest">
        <div class="px-4 py-3 text-sm font-bold uppercase text-green-darker">
          {{ $t('available_hours_qty') }}
        </div>
        <div class="px-4 py-3 text-right text-base font-bold text-green-darker">
          {{ formattedAvailableHours }}
        </div>
      </div>
    </div>

    <div class="mt-3 flex items-center gap-3">
      <button
        type="button"
        class="weekly-view-hour-premises__save-btn"
        :disabled="!isDirty || saving || !projectId"
        @click="savePremises"
      >
        {{ saving ? $t('saving') : $t('save') }}
      </button>
      <button
        v-if="isDirty && !saving"
        type="button"
        class="text-sm text-grey-dark underline"
        @click="resetFormFromSaved"
      >
        {{ $t('cancel') }}
      </button>
    </div>

    <p class="mt-3 text-xs text-grey-dark">
      {{ $t('hour_premises_formula') }}
    </p>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import { mergeProjectConfigs } from './merge-project-configs'

/** Vacío (0) hasta que el usuario configure y guarde en project.configs */
const EMPTY_PREMISES = {
  weeklyHours: 0,
  leaveLossPercent: 0,
  overtimePercent: 0,
  holidayLossPercent: 0,
}

function premisesFromConfig(configs) {
  const raw = (configs && configs.takeoff_premises) || {}
  return {
    weeklyHours: raw.weekly_hours != null ? Number(raw.weekly_hours) : 0,
    leaveLossPercent: raw.leave_loss_percent != null ? Number(raw.leave_loss_percent) : 0,
    overtimePercent: raw.overtime_hours_percent != null ? Number(raw.overtime_hours_percent) : 0,
    holidayLossPercent: raw.holiday_loss_percent != null ? Number(raw.holiday_loss_percent) : 0,
  }
}

function premisesToPayload(form) {
  return {
    weekly_hours: Number(form.weeklyHours) || 0,
    leave_loss_percent: Number(form.leaveLossPercent) || 0,
    overtime_hours_percent: Number(form.overtimePercent) || 0,
    holiday_loss_percent: Number(form.holidayLossPercent) || 0,
  }
}

export default {
  name: 'weekly-view-hour-premises',

  props: {
    projectId: {
      type: [Number, String],
      default: null,
    },
  },

  data() {
    return {
      form: { ...EMPTY_PREMISES },
      savedForm: { ...EMPTY_PREMISES },
      saving: false,
      inputRows: [
        { key: 'weeklyHours', labelKey: 'weekly_hours_qty', showPercent: false },
        { key: 'leaveLossPercent', labelKey: 'leave_loss_percent', showPercent: true },
        { key: 'overtimePercent', labelKey: 'overtime_hours_percent', showPercent: true },
        { key: 'holidayLossPercent', labelKey: 'holiday_loss_percent', showPercent: true },
      ],
    }
  },

  computed: {
    ...mapGetters({
      project: 'app/project',
    }),

    /**
     * QNT horas disponibles =
     * QTD horas semana × (1 - % folga) × (1 + % extras) × (1 - % feriado)
     */
    availableHours() {
      const weekly = Number(this.form.weeklyHours) || 0
      const leave = (Number(this.form.leaveLossPercent) || 0) / 100
      const overtime = (Number(this.form.overtimePercent) || 0) / 100
      const holiday = (Number(this.form.holidayLossPercent) || 0) / 100

      const value = weekly * (1 - leave) * (1 + overtime) * (1 - holiday)
      return Math.round(value * 1000) / 1000
    },

    formattedAvailableHours() {
      return this.availableHours.toFixed(3).replace('.', ',')
    },

    isDirty() {
      return Object.keys(EMPTY_PREMISES).some((key) => {
        const current = Number(this.form[key]) || 0
        const saved = Number(this.savedForm[key]) || 0
        return Math.abs(current - saved) > 0.0001
      })
    },
  },

  watch: {
    project: {
      immediate: true,
      handler() {
        this.loadFromProject()
      },
    },
  },

  methods: {
    loadFromProject() {
      const next = premisesFromConfig(this.project && this.project.configs)
      this.form = { ...next }
      this.savedForm = { ...next }
    },

    resetFormFromSaved() {
      this.form = { ...this.savedForm }
    },

    async savePremises() {
      if (!this.projectId || this.saving || !this.isDirty) {
        return
      }

      this.saving = true
      try {
        const payload = premisesToPayload(this.form)
        const configs = await mergeProjectConfigs(
          this.projectId,
          { takeoff_premises: payload },
          { store: this.$store },
        )

        const saved = premisesFromConfig(configs)
        this.form = { ...saved }
        this.savedForm = { ...saved }

        this.$notify.success('save_successful')
      } catch (error) {
        this.$notify.error('error_saving')
      } finally {
        this.saving = false
      }
    },
  },
}
</script>

<style scoped>
.weekly-view-hour-premises__input:focus {
  border-color: #0b4228;
  outline: none;
  box-shadow: 0 0 0 1px #0b4228;
}

.weekly-view-hour-premises__save-btn {
  background-color: #0b4228;
  color: #fff;
  font-size: 0.875rem;
  font-weight: 600;
  padding: 0.4rem 0.9rem;
  border-radius: 0.25rem;
  border: none;
  cursor: pointer;
}

.weekly-view-hour-premises__save-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
</style>
